import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { SelectedPacket } from 'components/enrollForm/enrollFormTypes'
import { generateUuidv4 } from 'helpers'

type UsePacketSelectionProps = {
    productPacket: SelectedPacket
    quantity: number
    packetSelectionHandler: Dispatch<SetStateAction<SelectedPacket[]>>
}

export const usePacketSelection = ({
    productPacket,
    quantity,
    packetSelectionHandler
}: UsePacketSelectionProps) => {
    const [counter, setCounter] = useState(quantity || 0)
    const [showCounterButton, setShowCounterButton] = useState(false)
    const [selectedPacket, setSelectedPacket] = useState(false)
    const packetRef = useRef<HTMLDivElement>(null)
    const prevCountRef = useRef<number>(counter)

    const {
        price,
        size,
        size_display: sizeDisplay,
        pricebook_id: pricebookId,
        package_id: packageId,
        product_id: productId
    } = productPacket

    // Handle click outside to close counter
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (
                packetRef.current &&
                !packetRef.current.contains(event.target as Node)
            ) {
                setShowCounterButton(false)
                setSelectedPacket(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (counter > prevCountRef.current) {
            packetSelectionHandler((prevPackets) => {
                const newPacket = {
                    ...productPacket,
                    uuid: generateUuidv4()
                }

                return prevPackets ? [...prevPackets, newPacket] : [newPacket]
            })
        }
        if (counter < prevCountRef.current) {
            packetSelectionHandler((prevPackets) => {
                if (!prevPackets) return prevPackets
                const foundPacket = prevPackets.find(
                    (packet) =>
                        packet.package_id === packageId &&
                        packet.product_id === productId
                )

                if (!foundPacket) return prevPackets

                const res = prevPackets.filter(
                    (packet) => packet.uuid !== foundPacket.uuid
                )
                return res
            })
        }
        prevCountRef.current = counter
    }, [
        counter,
        pricebookId,
        size,
        sizeDisplay,
        packetSelectionHandler,
        packageId
    ])

    const handlePacketSelection = () => {
        setSelectedPacket(true)
        setShowCounterButton(!showCounterButton)
        if (counter === 0) setCounter(1)
    }

    return {
        counter,
        setCounter,
        showCounterButton,
        selectedPacket,
        packetRef,
        price,
        size,
        sizeDisplay,
        pricebookId,
        packageId,
        handlePacketSelection
    }
}
